import type { ApiUser } from '@interfaces/api-user';
import type { Message } from '@interfaces/message';
import { useShallow } from 'zustand/react/shallow';

import { RightColumnContent } from '@/types';

import { useBoundStore } from '../index';

export function useActiveConversation() {
  return useBoundStore(
    useShallow((state) => {
      const slice = state.activeConversationId ? state.conversations.byId[state.activeConversationId] : null;

      return {
        conversation: slice?.resource ?? null,
        subscribed: slice?.subscribed ?? false,
        loadingMessages: slice?.isLoadingMessages ?? false,
        isLoading: state.conversations.isLoading,
      };
    }),
  );
}

export function useSharedActions() {
  return useBoundStore(
    useShallow((state) => ({
      setActiveConversationId: state.setActiveConversationId,
      loadMessages: state.loadConversationMessages,
      loadMessagesByDate: state.loadConversationMessagesByDate,
      loadUnreadMessages: state.loadUnreadMessages,
      exitForwardMode: state.exitForwardMode,
      openForwardMenu: state.openForwardMenu,
      setForwardChat: state.setForwardChat,
      fetchActiveConversationMembers: state.fetchActiveConversationMembers,
      toggleChatInfo: state.toggleChatInfo,
      openMiddleSearch: state.openMiddleSearch,
      closeMiddleSearch: state.closeMiddleSearch,
      updateMiddleSearch: state.updateMiddleSearch,
      resetMiddleSearch: state.resetMiddleSearch,
      performMiddleSearch: state.performMiddleSearch,
      selectSearchResult: state.selectSearchResult,
      focusMessage: state.focusMessage,
    })),
  );
}

export function useSender(message?: Message): ApiUser | undefined {
  return useBoundStore(
    useShallow((state) =>
      message
        ? message.user_id
          ? state.users.byUserId?.[message.user_id]
          : state.users.byTelegramUserId?.[message.telegram_user_id!]
        : undefined,
    ),
  );
}

export function useForwardedSender(message: Message): ApiUser | undefined {
  const { forward_info } = message;
  return useBoundStore(
    useShallow((state) =>
      forward_info
        ? forward_info.fromTGId
          ? state.users.byTelegramUserId?.[forward_info.fromTGId]
          : state.users.byUserId?.[forward_info.fromId]
        : undefined,
    ),
  );
}

export function useForwardedMessage(message: Message): Message | undefined {
  const { forward_info } = message;
  return useBoundStore(
    useShallow((state) => (forward_info ? state.messages.byId?.[forward_info.fromMessageId] : undefined)),
  );
}

export function useIsPendingForward(conversationId?: number) {
  return useBoundStore(
    useShallow((state) => conversationId !== undefined && state.forwardedMessage?.toChatId === conversationId),
  );
}

export function useRightColumnContentKey() {
  return useBoundStore((state) => (state.chatInfo.isOpen ? RightColumnContent.ChatInfo : undefined));
}

export function useIsRightColumnShown() {
  return useRightColumnContentKey() !== undefined;
}

export function useActiveConversationMembers() {
  return useBoundStore((state) => state.activeConversationMembers);
}

export function useCurrentMiddleSearch() {
  return useBoundStore((state) => {
    if (!state.activeConversationId) {
      return undefined;
    }

    return state.middleSearch.byConversationKey[`${state.activeConversationId}`];
  });
}

export function useHasActiveMiddleSearch() {
  const currentMiddleSearch = useCurrentMiddleSearch();
  return Boolean(currentMiddleSearch);
}

export function useFocusedMessageId(conversationId: number) {
  return useBoundStore((state) =>
    state.focusedMessage?.conversationId === conversationId ? state.focusedMessage.messageId : undefined,
  );
}

export function useMessageFocus(message: Message) {
  return useBoundStore((state) => {
    const focusedMessage = state.focusedMessage;
    if (focusedMessage?.conversationId !== message.conversation_id) {
      return undefined;
    }

    const isFocused = focusedMessage.messageId === message.id || focusedMessage.messageId === message.localId;

    return isFocused ? focusedMessage : undefined;
  });
}

export function useIsMessageFocused(message: Message) {
  const focusedMessage = useMessageFocus(message);

  return Boolean(focusedMessage);
}
